/**
 * DOMAIN:
 * this is for the Merchant Pricing page.
 *
 * STATE:
 * - List important state properties
 *
 *
 * SIDE EFFECTS:
 * - API calls handled here via service
 * - Get Pricing
 * - Validate pricing
 * - Save
 *
 * FLOW CONTROL:
 * - Step transitions?
 * - Authentication?
 *
 * USED BY:
 * - Merchant Pricing page
 * - Tiered pricing component
 */

import { computed, inject } from '@angular/core';

import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ApiResponse } from 'app/models/api-response';
import {
    CustomReferenceNumber,
    isTabPricingEqual,
    Pricing,
    PricingRange,
    PricingSetUpEnum,
    PricingTypeEnum,
} from 'app/models/pricing-model';
import { PageError } from 'app/modules/error/error.component';
import { ErrorService } from 'app/modules/error/service/error.service';
import { PricingService } from 'app/services/pricing.service';
import { EMPTY, pipe, switchMap, tap } from 'rxjs';
import { LoggedOnMerchantSummaryStore } from '../merchant.store';

export enum PricingActionEnum {
    get = 'GET',
    create = 'CREATE',
    update = 'UPDATE',
    delete = 'DELETE',
}

type PricingState = {
    merchantApiId: string | null;
    model: ApiResponse<Pricing[]> | null;
    pricingTables: Pricing[];
    savedPricingTables: Pricing[];
    pricingRanges: Record<number, PricingRange[]>;
    customReferenceNumbers: Record<number, CustomReferenceNumber[]>;
    activeTab: number;
    hasChanges: boolean;
    isFormValid: boolean;
    isLoading: boolean;
    success: boolean;
    errorList: string[] | null;
    error: string | null;
    hasSavedPricingConfig: boolean;
    customPricingCount: number;
    hasSavedGeneralAcceptanceConfig: boolean;
    tabEdited: number | null;
    action: PricingActionEnum;
    // tabCustomReferenceNumbers: CustomReferenceNumber[] | [];
    isReferenceFormValid: boolean;
    pricingConfigsMap: Record<number, Pricing>;
    originalPricingMap: Record<number, Pricing>;
    tabValidityMap: Record<number, boolean>;
    isNewMap: Record<number, boolean>;
    gstInclusiveMap: Record<number, boolean>;
    tabCustomReferenceValityMap: Record<number, boolean>;
};

const initialState: PricingState = {
    merchantApiId: null,
    model: null as ApiResponse<Pricing[]> | null,
    pricingTables: [],
    savedPricingTables: [],
    pricingRanges: {} as Record<number, PricingRange[]>,
    customReferenceNumbers: {} as Record<number, CustomReferenceNumber[]>,
    activeTab: 0,
    hasChanges: false,
    isFormValid: false,
    isLoading: false,
    success: false,
    errorList: null,
    error: null,
    hasSavedPricingConfig: false,
    customPricingCount: 0,
    hasSavedGeneralAcceptanceConfig: false,
    tabEdited: null,
    action: PricingActionEnum.get,
    // tabCustomReferenceNumbers: [],
    isReferenceFormValid: false,
    pricingConfigsMap: {} as Record<number, Pricing>,
    originalPricingMap: {} as Record<number, Pricing>,
    tabValidityMap: {} as Record<number, boolean>,
    isNewMap: {} as Record<number, boolean>,
    gstInclusiveMap: {} as Record<number, boolean>,
    tabCustomReferenceValityMap: {} as Record<number, boolean>,
};

export const MerchantPricingStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed(
        ({
            pricingTables,
            activeTab,
            pricingRanges,
            customReferenceNumbers,
            // tabCustomReferenceNumbers,
            tabEdited,
        }) => ({
            activePricingTable: computed(() => {
                return pricingTables()[activeTab()];
            }),
            activeTiers: computed(() => {
                const tab = activeTab();
                return pricingRanges()[tab] ?? [];
            }),
            activeCustomerReferenceNumbers: computed(() => {
                return customReferenceNumbers()[activeTab()];
            }),
            // referenceNumbers: computed(() => {
            //     if (activeTab() === tabEdited()) {
            //         return tabCustomReferenceNumbers();
            //     }
            // }),
        })
    ),
    withComputed((store) => ({
        isTabCustomReferenceValid: computed(() => (tabIndex: number) => {
            return store.tabCustomReferenceValityMap()[tabIndex] ?? true;
        }),
        isTabValid: computed(() => (tabIndex: number): boolean => {
            return store.tabValidityMap()[tabIndex] ?? true;
        }),
        // wizard pricing continue button
        disableButton: computed(() => {
            const ga = store.pricingTables()[0];
            const rows = ga.pricingRanges;
            const isEmpty = rows.length === 0;
            return isEmpty;
        }),
        canSave: computed(() => {
            return store.isFormValid();
        }),
        canAddCustomConfig: computed(() => {
            const canAdd =
                store.hasSavedGeneralAcceptanceConfig() &&
                store.pricingTables().filter((config) => !config.pricingTableId || !config.pricingTableId.trim())
                    .length === 0;
            return canAdd;
        }),
        // selector for a specific tab
        tabHasChanges: computed(() => (tabIndex: number): boolean => {
            if (store.isNewMap()[tabIndex]) {
                return true;
            }
            const original = store.originalPricingMap()[tabIndex];
            const current = store.pricingConfigsMap()[tabIndex];

            if (!current) return false;
            if (!original) return true;

            // console.log(isTabPricingEqual(original, current), tabIndex);
            const isEqual = isTabPricingEqual(original, current);
            // if (!isEqual) {
            //     console.log('original', original);
            //     console.log('current', current);
            //     console.log('has changes', !isEqual);
            // }
            return !isEqual;
        }),
        hasAnyTabChanges: computed((): boolean => {
            const originals = store.originalPricingMap();
            const currents = store.pricingConfigsMap();

            // console.log('originals', originals);
            // console.log('current', currents);

            return Object.keys(originals).some((key) => {
                const index = Number(key);

                return !isTabPricingEqual(originals[index], currents[index]);
            });
        }),
    })),
    withMethods((store) => ({
        updateTabCustomReferenceValidity(tabIndex: number, isValid: boolean) {
            patchState(store, (state) => ({
                tabCustomReferenceValityMap: {
                    ...state.tabCustomReferenceValityMap,
                    [tabIndex]: isValid,
                },
            }));
        },
        // update tab validity
        updateTabValidity(tabIndex: number, isValid: boolean) {
            patchState(store, (state) => ({
                tabValidityMap: {
                    ...state.tabValidityMap,
                    [tabIndex]: isValid,
                },
            }));
        },
        // Initialise API payload for a specific tab (baseline + working state)
        setInitialTabPricing(tabIndex: number, pricing: Pricing) {
            const baseline = structuredClone(pricing);
            const working = structuredClone(pricing);

            patchState(store, (state) => ({
                originalPricingMap: {
                    ...state.originalPricingMap,
                    [tabIndex]: baseline,
                },
                pricingConfigsMap: {
                    ...state.pricingConfigsMap,
                    [tabIndex]: working,
                },
            }));
        },

        // Update working pricing configuration
        updateTabPricing(tabIndex: number, partial: Partial<Pricing>) {
            const current = store.pricingConfigsMap()[tabIndex] ?? ({} as Pricing);

            patchState(store, (state) => ({
                pricingConfigsMap: {
                    ...state.pricingConfigsMap,
                    [tabIndex]: {
                        ...current,
                        ...partial,
                    },
                },
            }));
        },

        // update specifically the pricingRanges
        updateTabRanges(tabIndex: number, ranges: PricingRange[]) {
            const current = store.pricingConfigsMap()[tabIndex] ?? ({} as Pricing);
            patchState(store, (state) => ({
                pricingConfigsMap: {
                    ...state.pricingConfigsMap,
                    [tabIndex]: {
                        ...current,
                        pricingRanges: ranges,
                    },
                },
            }));
        },

        // call after a successful http save to update the baseline
        markTabSaved(tabIndex: number, savedPricingData?: Pricing) {
            const current = store.pricingConfigsMap()[tabIndex];

            if (!current) return;

            patchState(store, (state) => {
                const updatedPricing = savedPricingData ?? state.pricingConfigsMap[tabIndex];

                return {
                    isNewMap: {
                        ...state.isNewMap,
                        [tabIndex]: false,
                    },
                    pricingConfigsMap: {
                        ...state.pricingConfigsMap,
                        [tabIndex]: structuredClone(updatedPricing),
                    },
                    originalPricingMap: {
                        ...state.originalPricingMap,
                        [tabIndex]: structuredClone(current),
                    },
                };
            });
        },
    })),
    withMethods(
        (
            store,
            service = inject(PricingService),
            snackBar = inject(MatSnackBar),
            merchantStore = inject(LoggedOnMerchantSummaryStore),
            router = inject(Router),
            errorHandler = inject(ErrorService)
        ) => {
            const gotoPageError = (
                title: string = 'Request Exceeded',
                customMessage = "To keep things running smoothly for everyone, we've temporarily limited your access. Please wait a moment and try again shortly."
            ) => {
                const pageError: PageError = {
                    title,
                    message: customMessage,
                    icon: 'mat_outline:arrow_back',
                };
                errorHandler.setError(pageError);
                router.navigate(['/error'], { replaceUrl: true });
            };

            // Helper to handle the specific rate limit logic across all side effects
            const handleApiError = (err: any) => {
                const isRateLimit = err?.status === 429 || err?.error?.code === 'RATE_LIMIT';

                patchState(store, {
                    isLoading: false,
                    success: false,
                    error: isRateLimit
                        ? "Request Exceeded. To keep things running smoothly for everyone, we've temporarily limited your access. Please wait a moment and try again shortly."
                        : 'Something went wrong, please try again later.',
                });

                if (isRateLimit) {
                    gotoPageError();
                }
            };

            return {
                getMerchantPricing: rxMethod<string>(
                    pipe(
                        // distinctUntilChanged(),
                        tap(() =>
                            patchState(store, {
                                isLoading: true,
                                success: false,
                                error: null,
                                errorList: null,
                            })
                        ),
                        switchMap((merchantId) => {
                            return service.getPricing(merchantId).pipe(
                                tapResponse({
                                    next: (model) => {
                                        // Get response
                                        /* console.log(
                                            'Get Response: ',
                                            model.data
                                        ); */

                                        const pricingRanges: Record<number, PricingRange[]> = {};

                                        const customReferenceNumbers: Record<number, CustomReferenceNumber[]> = {};

                                        let pricingTables: Pricing[];
                                        if (!model.data || model.data.length === 0) {
                                            pricingTables = [
                                                {
                                                    label: PricingTypeEnum.general,
                                                    pricingType: PricingTypeEnum.general,
                                                    pricingSetUp: PricingSetUpEnum.notConfigured,
                                                    pricingRanges: [],
                                                    customReferenceNumbers: [],
                                                    // includeGST: false,
                                                },
                                            ];

                                            patchState(store, {
                                                hasSavedPricingConfig: false,
                                                hasSavedGeneralAcceptanceConfig: false,
                                                customPricingCount: 0,
                                                merchantApiId: merchantId,
                                            });
                                        } else {
                                            pricingTables = model.data;

                                            // Determine if there is a general acceptance config saved
                                            const generalAcceptance = pricingTables.filter(
                                                (pt) => pt.pricingType == PricingTypeEnum.general
                                            );
                                            if (generalAcceptance?.length > 0) {
                                                patchState(store, {
                                                    hasSavedGeneralAcceptanceConfig: true,
                                                    merchantApiId: merchantId,
                                                });
                                            } else {
                                                patchState(store, {
                                                    hasSavedGeneralAcceptanceConfig: false,
                                                    merchantApiId: merchantId,
                                                });
                                            }
                                        }
                                        let cstmPricingCount = 0;
                                        pricingTables.forEach((config, i) => {
                                            pricingRanges[i] = config.pricingRanges ?? [];

                                            customReferenceNumbers[i] = config.customReferenceNumbers ?? [];

                                            // increment the Custom Pricing count
                                            if (config.pricingType == 'Custom Pricing') {
                                                ++cstmPricingCount;
                                            }
                                        });

                                        patchState(store, {
                                            merchantApiId: merchantId,
                                            isLoading: false,
                                            success: true,
                                            model,
                                            savedPricingTables: pricingTables,
                                            pricingTables,
                                            pricingRanges,
                                            customPricingCount: cstmPricingCount,
                                            customReferenceNumbers: customReferenceNumbers,
                                            hasSavedPricingConfig: true,
                                            tabEdited: null,
                                            hasChanges: false,
                                            action: PricingActionEnum.get,
                                        });
                                    },
                                    error: (err) => handleApiError(err),
                                })
                            );
                        })
                    )
                ),
                deletePricing: rxMethod<any>(
                    pipe(
                        tap(() =>
                            patchState(store, {
                                isLoading: true,
                                success: false,
                                error: null,
                                errorList: null,
                            })
                        ),
                        switchMap((pricingTableId) => {
                            return service.deletePricing(pricingTableId).pipe(
                                tapResponse({
                                    next: (deleteResult) => {
                                        if (deleteResult.isSuccess) {
                                            snackBar.open('Pricing tab has been successfully deleted.', 'Success', {
                                                verticalPosition: 'top',
                                                duration: 3000,
                                            });

                                            patchState(store, {
                                                isLoading: false,
                                                success: true,
                                                hasChanges: false,
                                                tabEdited: null,
                                                action: PricingActionEnum.delete,
                                            });

                                            // delete the config from pricing table
                                            /* console.log(
                                                'Before delete existing: ',
                                                store.pricingTables()
                                            ); */

                                            const indexToDelete = store
                                                .pricingTables()
                                                .findIndex((x) => x.pricingTableId == pricingTableId);
                                            /* console.log(
                                                'Index to delete: ',
                                                indexToDelete
                                            ); */

                                            const tabs = [...store.pricingTables()];

                                            /* const tabIndex = store.activeTab();
                                            if (tabIndex === -1) return; */

                                            tabs.splice(indexToDelete, 1);

                                            patchState(store, {
                                                pricingTables: tabs,
                                                savedPricingTables: tabs,
                                            });

                                            /* console.log(
                                                'after delete existing: ',
                                                store.pricingTables()
                                            ); */
                                        } else {
                                            const list = deleteResult.data.errors
                                                ? deleteResult.data.errors.map(
                                                      (err: any) => `Tier ${err.index + 1}: ${err.message}`
                                                  )
                                                : [deleteResult.message];
                                            patchState(store, {
                                                isLoading: false,
                                                success: false,
                                                errorList: list,
                                            });
                                            return EMPTY;
                                        }
                                    },
                                    error: (err) => handleApiError(err),
                                })
                            );
                        })
                    )
                ),
                savePricing: rxMethod<any>(
                    pipe(
                        //distinctUntilChanged(),
                        tap(() =>
                            patchState(store, {
                                isLoading: true,
                                success: false,
                                error: null,
                                errorList: null,
                            })
                        ),
                        switchMap((data) => {
                            const merchantApiId = store.merchantApiId();

                            const payload = {
                                merchantApiId,
                                pricingName: data.label,
                                pricingType: data.pricingType,
                                pricingRanges: data.pricingRanges,
                                pricingSetUp: data.pricingSetup,
                                pricingTableId: data.pricingTableId,
                                includeGST: data.includeGST,
                                customReferenceNumbers: data.customReferenceNumbers,
                            };

                            return service.savePricing(payload).pipe(
                                tapResponse({
                                    next: (saveResult) => {
                                        if (saveResult.isSuccess) {
                                            const tabs = [...store.pricingTables()];

                                            const tabIndex = store.activeTab();
                                            if (tabIndex === -1) return;
                                            tabs[tabIndex] = {
                                                ...tabs[tabIndex],
                                                ...saveResult.data.at(0),
                                            };

                                            patchState(store, (state) => ({
                                                savedPricingTables: tabs,
                                                pricingTables: tabs,
                                                isLoading: false,
                                                success: true,
                                                error: null,
                                                errorList: null,
                                                action: PricingActionEnum.update,
                                                hasChanges: false,
                                                tabEdited: null, // to set tabHasChanges to false
                                                isNewMap: {
                                                    ...state.isNewMap,
                                                    [tabIndex]: false,
                                                },
                                                pricingConfigsMap: {
                                                    ...state.pricingConfigsMap,
                                                    [tabIndex]: structuredClone(tabs[tabIndex]),
                                                },
                                                originalPricingMap: {
                                                    ...state.originalPricingMap,
                                                    [tabIndex]: structuredClone(tabs[tabIndex]),
                                                },
                                            }));

                                            // Check if saved was General Acceptance
                                            if (payload.pricingType === PricingTypeEnum.general) {
                                                patchState(store, {
                                                    hasSavedGeneralAcceptanceConfig: true,
                                                });
                                            }

                                            snackBar.open(saveResult.message, 'Success', {
                                                verticalPosition: 'top',
                                                duration: 3000,
                                            });
                                        } else if (saveResult.data.errors.length > 0) {
                                            const list = saveResult.data.errors
                                                ? saveResult.data.errors.map((err: any) => err.message)
                                                : [saveResult.message];
                                            patchState(store, {
                                                isLoading: false,
                                                success: false,
                                                errorList: list,
                                            });
                                            return EMPTY;
                                        } else {
                                            patchState(store, {
                                                isLoading: false,
                                                success: false,
                                                error: saveResult.message,
                                            });
                                        }
                                    },
                                    error: (err) => handleApiError(err),
                                })
                            );
                        })
                    )
                ),
                validatePricing: (data: any) => {
                    // 1. Initial state update (previously in the top-level tap)
                    patchState(store, {
                        error: null,
                        errorList: null,
                    });

                    const merchantApiId = store.merchantApiId();

                    const payload = {
                        merchantApiId,
                        pricingName: data.label,
                        pricingType: data.pricingType,
                        pricingRanges: data.pricingRanges,
                        pricingSetUp: data.pricingSetup,
                        pricingTableId: data.pricingTableId,
                        includeGST: data.includeGST,
                        customReferenceNumbers: data.customReferenceNumbers,
                    };

                    // 2. Return the Observable directly
                    return service.validatePricing(payload).pipe(
                        tapResponse({
                            next: (validateResult: any) => {
                                if (validateResult.isSuccess) {
                                    patchState(store, {
                                        error: null,
                                        errorList: null,
                                    });
                                } else if (validateResult.data?.errors?.length > 0) {
                                    const list = validateResult.data.errors
                                        ? validateResult.data.errors.map((err: any) => err.message)
                                        : [validateResult.message];

                                    patchState(store, {
                                        errorList: list,
                                    });
                                } else {
                                    patchState(store, {
                                        error: validateResult.message,
                                    });
                                }
                            },
                            error: (err: any) => handleApiError(err),
                        })
                    );
                },
            };
        }
    ),
    withMethods((store) => ({
        setActiveTab(tabIndex: number) {
            patchState(store, { activeTab: tabIndex });
        },

        setHasChanges(value: boolean, tabIndex: number) {
            patchState(store, { hasChanges: value, tabEdited: tabIndex });
        },

        setFormValidity(isValid: boolean) {
            patchState(store, { isFormValid: isValid });
        },

        setReferenceFormValidity(isValid: boolean) {
            patchState(store, { isReferenceFormValid: isValid });
        },

        updateCustomReferenceNumberByTab(refs: CustomReferenceNumber[], tabIndex: number) {
            patchState(store, {
                customReferenceNumbers: { ...store.customReferenceNumbers(), [tabIndex]: refs },
                tabEdited: tabIndex,
                hasChanges: true,
            });
        },

        updatePricingRanges(rows: PricingRange[], tabIndex: number) {
            patchState(store, {
                pricingRanges: {
                    ...store.pricingRanges(),
                    [tabIndex]: rows,
                },
                tabEdited: tabIndex,
                hasChanges: true,
            });
        },

        reset() {
            patchState(store, initialState);
        },

        resetDirtyState() {
            patchState(store, { hasChanges: false, tabEdited: null });
        },

        resetToBackup() {
            const masterBackup = structuredClone(store.savedPricingTables());
            patchState(store, { hasChanges: false, tabEdited: null, pricingTables: masterBackup });
        },

        removePricingConfigFromBackup: (indexToRemove: number) => {
            const updatedTables = [...store.pricingTables()];
            const updatedBackup = [...store.savedPricingTables()];

            if (indexToRemove > -1) {
                updatedTables.splice(indexToRemove, 1);
                updatedBackup.splice(indexToRemove, 1);
            }

            patchState(store, {
                pricingTables: updatedTables,
                savedPricingTables: updatedBackup,
                hasChanges: false,
                tabEdited: null,
            });
        },

        addPricingConfig(pricingConfig: Pricing) {
            const tables = store.pricingTables() ?? [];
            const updatedTables = [...tables, pricingConfig];

            patchState(store, {
                pricingTables: updatedTables,
                activeTab: updatedTables.length - 1, // switch to new tab
            });
        },

        addNewCustomTabPricing(tabIndex: number, newPricing: Pricing) {
            const tables = store.pricingTables() ?? [];
            const updated = [...tables, newPricing];
            patchState(store, (state) => ({
                pricingTables: [...updated],
                pricingConfigsMap: {
                    ...state.pricingConfigsMap,
                    [tabIndex]: newPricing,
                },
                originalPricingMap: {
                    ...state.originalPricingMap,
                    [tabIndex]: null as any,
                },
                isNewMap: {
                    ...state.isNewMap,
                    [tabIndex]: true,
                },
                activeTab: tabIndex,
            }));
        },
        deletePricingConfig(tabIndex: number) {
            //console.log('Before delete: ', store.pricingTables().length);

            const tabs = [...store.pricingTables()];

            if (tabIndex === -1) return;

            tabs.splice(store.activeTab(), 1);

            patchState(store, {
                pricingTables: tabs,
            });

            //console.log('after delete: ', store.pricingTables().length);
        },
        isConfigNew(tabIndex: number) {
            return (
                !store.pricingTables().at(tabIndex)?.pricingTableId ||
                !store.pricingTables().at(tabIndex)?.pricingTableId?.trim()
            );
        },
        resetError() {
            patchState(store, { error: null, errorList: null });
        },
        setMerchantApiId(merchantApiId: string) {
            patchState(store, { merchantApiId: merchantApiId });
        },
    }))
);
